import React, {
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabaseClient'; // Ensure this path is correct
import { metadataTranslations } from '../constants/translations'; // Ensure this path is correct
import {
  Box,
  Slider,
  Grid,
  Typography,
  Modal,
  CardContent,
  CardMedia,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Fade,
  InputBase,
  Button,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CompareIcon from '@mui/icons-material/Compare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'; // Import video icon

import CustomTooltip from './CustomTooltip'; // Ensure this component exists and path is correct
import ImageCompare from './ImageCompare'; // Ensure this component exists and path is correct
import { styled } from '@mui/system';

// Removed unused Meteors and Particles imports

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const StyledImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor:
    theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
  transition: 'all 0.3s ease-in-out',
  opacity: 1,
  '&:hover': {
    filter: 'brightness(1.1) contrast(1.1)',
  },
}));

const Gallery = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef(null);

  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]); // Renamed from images -> items
  const [imagesPerRow, setImagesPerRow] = useState(isMobile ? 2 : 4); // Default to 2 on mobile
  const [selectedItem, setSelectedItem] = useState(null); // Renamed from selectedImage -> selectedItem
  const [loadingFullImage, setLoadingFullImage] = useState(false); // Used for modal image/video loading
  const [visibleItems, setVisibleItems] = useState([]); // Renamed from visibleImages -> visibleItems
  const [loading, setLoading] = useState(true); // General loading state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]); // Renamed from filteredImages -> filteredItems
  const [expandedMetadata, setExpandedMetadata] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0: All, 1: Properties, 2: Images, 3: Videos
  const [filterType, setFilterType] = useState('all'); // 'all', 'image', 'video' - Set by tabs
  const [anchorEl, setAnchorEl] = useState(null); // For item menu
  const [createPropertyDialogOpen, setCreatePropertyDialogOpen] =
    useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [properties, setProperties] = useState([]); // List of user's properties for menu

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signup', { // Redirect to signup/login if not authenticated
          state: {
            returnUrl: '/gallery',
            message:
              'Galerinizi görüntülemek için lütfen giriş yapın veya kaydolun',
          },
        });
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch images AND videos for a user id
  const fetchItems = useCallback(async (userId) => {
    if (!userId) {
      console.log("Kullanıcı ID'si bulunamadı, öğeler yüklenemiyor.");
      setLoading(false); // Stop loading if no user ID
      setItems([]); // Clear items
      setFilteredItems([]);
      return;
    }

    setLoading(true);
    try {
      // Fetch images
      const { data: imageData, error: imageError } = await supabase
        .from('images')
        .select('*')
        .eq('user_id', userId);

      if (imageError) {
        console.error('Resimler yüklenirken hata:', imageError);
        // Potentially show an error message to the user
      }

      // Fetch videos
      const { data: videoData, error: videoError } = await supabase
        .from('videos')
        .select('*')
        .eq('user_id', userId);

      if (videoError) {
        console.error('Videolar yüklenirken hata:', videoError);
        // Potentially show an error message to the user
      }

      // Map images to common structure
      const mappedImages = (imageData || []).map(img => ({
        ...img,
        type: 'image',
        thumbnailUrl: img.url, // Images use their own URL as thumbnail
        // Ensure essential fields exist, provide defaults if necessary
        prompt: img.prompt || '',
        created_at: img.created_at || new Date().toISOString(),
      }));

      // Map videos to common structure
      const mappedVideos = (videoData || []).map(vid => ({
        ...vid,
        type: 'video',
        // Use a placeholder if no thumbnail or source image is available
        thumbnailUrl: vid.thumbnail_url || vid.source_image_url || '/placeholder-video.png',
        prompt: vid.title || '', // Use title as searchable text like prompt
        // Ensure essential fields exist, provide defaults if necessary
        created_at: vid.created_at || new Date().toISOString(),
        // Include other video-specific fields if needed directly
        // e.g., duration: vid.duration, camera_movement: vid.camera_movement
      }));

      // Combine and sort by creation date (newest first)
      const combinedItems = [...mappedImages, ...mappedVideos].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setItems(combinedItems);

    } catch (error) {
      console.error('Öğeler yüklenirken beklenmeyen hata:', error);
      // Show a generic error message
    } finally {
      setLoading(false);
    }
  }, []);


  // Fetch user's properties (used for the 'Add to Property' menu)
  const fetchProperties = useCallback(async (userId) => {
      if (!userId) return;
      try {
          const { data, error } = await supabase
              .from('properties')
              .select('id, name') // Only fetch needed fields
              .eq('user_id', userId)
              .order('created_at', { ascending: false });

          if (error) {
              console.error('Mevcut mülkler yüklenirken hata:', error);
          } else {
              setProperties(data || []);
          }
      } catch (error) {
          console.error('Mülkler yüklenirken beklenmeyen hata:', error);
      }
  }, []);

  // Initialize gallery and set up auth state listener
  useEffect(() => {
    let mounted = true;

    const initializeGallery = async () => {
      const {
        data: { session }, error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError) {
          console.error("Error getting session:", sessionError);
          // Handle error appropriately, maybe redirect to login
          if (mounted) setLoading(false);
          return;
      }

      if (mounted) {
        setSession(session);
        if (session?.user?.id) {
          fetchItems(session.user.id);
          fetchProperties(session.user.id); // Fetch properties too
        } else {
          setLoading(false); // Not logged in, stop loading
          // No need to navigate here, the initial checkAuth effect handles it
        }
      }
    };

    initializeGallery();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        const userId = session?.user?.id;
        setSession(session); // Update session state regardless
        if (userId) {
          // User is logged in or state changed while logged in
          fetchItems(userId);
          fetchProperties(userId);
        } else {
          // User logged out or session became invalid
          setItems([]);
          setProperties([]);
          setFilteredItems([]);
          setVisibleItems([]); // Clear visible items too
          setLoading(false); // Stop loading
          // Redirect to home or login after logout
          navigate('/', { replace: true });
        }
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [fetchItems, fetchProperties, navigate]); // Add fetchProperties dependency

  // Update filtered items based on the search query and filter type
  useEffect(() => {
    const updateFilteredItems = () => {
      let currentItems = [...items]; // Start with all items

      // Apply type filter first
      if (filterType !== 'all') {
          currentItems = currentItems.filter(item => item.type === filterType);
      }

      // Apply search query filter
      if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          currentItems = currentItems.filter((item) => {
              // Searchable fields (prompt includes image prompt & video title)
              const searchableFields = [
                  item.prompt, // Includes image prompt & video title
                  item.type === 'image' ? item.negative_prompt : null, // Only for images
                  item.type === 'image' ? item.style : null,
                  item.type === 'image' ? item.mode : null,
                  item.type === 'image' ? item.workflow_title : null,
                  item.type === 'image' ? item.enhanced_prompt : null,
                  item.property_name, // Search by linked property name
              ].filter(field => typeof field === 'string'); // Ensure fields are strings and filter out null/undefined

              return searchableFields.some(
                  (field) => field.toLowerCase().includes(query)
              );
          });
      }

      setFilteredItems(currentItems);
      setVisibleItems([]); // Reset visible items when filters change to re-trigger observer
    };

    // Debounce the filtering slightly
    const timeoutId = setTimeout(updateFilteredItems, 150); // Increased debounce slightly
    return () => clearTimeout(timeoutId);
  }, [searchQuery, items, filterType]); // Depend on search, all items, and filter type

  // Intersection Observer for lazy rendering of items
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const itemId = entry.target.dataset.id;
            if (itemId) {
                setVisibleItems((prevVisible) => {
                  // Use Set for efficient check and add
                  const visibleSet = new Set(prevVisible);
                  if (!visibleSet.has(itemId)) {
                    visibleSet.add(itemId);
                    return Array.from(visibleSet);
                  }
                  return prevVisible; // Return previous array if already visible (avoids re-render)
                });
                // Optional: Unobserve after becoming visible if performance is an issue and items don't move
                // observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Load items further before they enter viewport
        threshold: 0.01, // Trigger even if only a small part is visible
      }
    );

    // Observe elements based on the currently filtered items
    // Query elements within the containerRef
    const itemElements =
      containerRef.current?.querySelectorAll('.grid-item-observer-target'); // Use a specific class

    if (itemElements && itemElements.length > 0) {
        itemElements.forEach((element) => {
          observer.observe(element);
        });
    }


    // Cleanup: Disconnect observer when component unmounts or filteredItems change significantly
    return () => {
      // Disconnect observer fully on cleanup
      observer.disconnect();
    };
  }, [filteredItems]); // Re-run observer setup when filtered items change

  const handleSliderChange = useCallback((event, newValue) => {
    setImagesPerRow(newValue);
  }, []);

  // Handles clicking on an item grid card
  const handleItemClick = useCallback((item, event) => {
    // Prevent modal opening if the click is on the menu button area
    // Check if the click target or its parent is the menu button
    if (event?.target.closest('.menu-button')) {
        return;
    }
    // Prevent modal opening if the menu itself is already open
    if (anchorEl) {
      return;
    }

    // Set the selected item to open the modal (remove forMenu flag if present)
    const viewItem = { ...item };
    delete viewItem.forMenu;
    setSelectedItem(viewItem);
    setLoadingFullImage(true); // Show loading indicator in modal until media loads
    setExpandedMetadata(false); // Collapse metadata on new selection
  }, [anchorEl]);

  // Called when the main image/video in the modal has loaded
  const handleImageLoad = useCallback(() => {
    setLoadingFullImage(false);
  }, []);

  // Handles closing the main item detail modal
  const handleCloseModal = useCallback(() => {
    // Use MUI Fade's exit animation - just set selectedItem to null
    setSelectedItem(null);
    setShowCompare(false); // Also close compare modal if open
    // No need for 'closing' state and setTimeout with Fade component managing exit
  }, []);

  const copyToClipboard = (text) => {
    if (text && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          console.log('Metin panoya kopyalandı');
          // TODO: Optional: Show a temporary success message/toast
        }, (err) => {
          console.error('Panoya kopyalanamadı: ', err);
          // TODO: Optional: Show an error message
        });
    } else {
        console.warn('Clipboard API desteklenmiyor veya kopyalanacak metin yok.');
        // Fallback or message for older browsers/no text
    }
  };

  // Handles closing the item action menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    // Clear the 'forMenu' item state when menu closes
    // No need for setTimeout hack if handleItemClick checks anchorEl state
    setSelectedItem(prev => (prev?.forMenu ? null : prev));
  };


  // Handles deleting an item (image or video)
  const handleDeleteItem = useCallback(
    async (itemToDelete, event) => {
      if (!itemToDelete || !itemToDelete.id || !itemToDelete.type) {
        console.error('Silinecek geçerli öğe bulunamadı.');
        handleMenuClose(); // Close menu even on error
        return;
      }

      event?.stopPropagation(); // Prevent background clicks
      const itemTypeDisplay = itemToDelete.type === 'image' ? 'resmi' : 'videoyu';
      const currentItemId = itemToDelete.id; // Store ID before closing menu potentially clears state

      handleMenuClose(); // Close menu immediately

      if (window.confirm(`Bu ${itemTypeDisplay} silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
        setLoading(true); // Indicate loading state (could be more specific)
        try {
          const tableName = itemToDelete.type === 'image' ? 'images' : 'videos';
          const { error } = await supabase
            .from(tableName)
            .delete()
            .eq('id', currentItemId); // Use stored ID

          if (error) throw error;

          // Optional: Delete from storage if applicable (usually handled by triggers/functions)
          // Example (needs careful implementation based on your storage structure):
          // if (itemToDelete.url) {
          //   const path = new URL(itemToDelete.url).pathname.split('/render/').pop(); // Adjust based on your URL structure
          //   if (path) {
          //     const { error: storageError } = await supabase.storage.from('your-bucket-name').remove([path]);
          //     if (storageError) console.error("Storage deletion error:", storageError);
          //   }
          // }

          // Remove item from local state
          setItems((prevItems) =>
            prevItems.filter((i) => i.id !== currentItemId)
          );
          // If the deleted item was selected in the modal, close the modal
          if (selectedItem && selectedItem.id === currentItemId && !selectedItem.forMenu) {
            handleCloseModal();
          }

          console.log(`${itemTypeDisplay} başarıyla silindi.`);
          // TODO: Optional: Show success notification

        } catch (error) {
          console.error(`${itemTypeDisplay} silinirken hata:`, error);
          alert(`${itemTypeDisplay} silinemedi. Lütfen tekrar deneyin veya konsolu kontrol edin.`);
          // TODO: Optional: Show error notification
        } finally {
          setLoading(false); // End loading state
        }
      }
    },
    [selectedItem, handleCloseModal] // Dependencies
  );


  // Handles tab changes for filtering (All, Properties, Images, Videos)
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchQuery(''); // Clear search when changing tabs

    if (newValue === 1) { // Properties Tab
      navigate('/properties', { replace: false }); // Navigate to properties page
      setFilterType('all'); // Reset filter type as we are leaving gallery view
    } else {
      // Only navigate if we're not already on the gallery page
      if (window.location.pathname !== '/gallery' && newValue !== tabValue) {
          navigate('/gallery', { replace: true }); // Use replace to avoid history clutter
      }
      // Set filter type based on the selected gallery tab
      if (newValue === 0) setFilterType('all');      // All
      else if (newValue === 2) setFilterType('image'); // Images
      else if (newValue === 3) setFilterType('video'); // Videos
    }
  };

  // Opens the action menu for a specific item
  const handleMenuOpen = (event, item) => {
    event.stopPropagation(); // Prevent triggering handleItemClick
    event.preventDefault(); // Prevent any default behavior
    setAnchorEl(event.currentTarget); // Set anchor for the menu
    // Store the item intended for menu actions, marking it as such
    setSelectedItem({ ...item, forMenu: true });
  };

  // Adds the currently selected menu item to an existing property
  const handleAddToProperty = async (propertyId, propertyName) => {
      if (!selectedItem || !selectedItem.forMenu || !propertyId || !selectedItem.type) {
          console.error("Mülke eklenecek öğe veya mülk ID'si eksik.");
          handleMenuClose();
          return;
      }

      const itemToAdd = { ...selectedItem }; // Work with a copy
      handleMenuClose(); // Close menu immediately
      setLoading(true); // Show loading (consider more specific loading state)

      try {
          const tableName = itemToAdd.type === 'image' ? 'images' : 'videos';
          const { data, error } = await supabase
              .from(tableName)
              .update({
                  property_id: propertyId,
                  property_name: propertyName, // Store property name for easier display
              })
              .eq('id', itemToAdd.id)
              .select() // Select the updated row to confirm
              .single(); // Expect a single row back

          if (error) throw error;
          if (!data) throw new Error("Öğe güncellenemedi veya bulunamadı.");

          // Update local state for the item to reflect the change
          setItems((prev) =>
              prev.map((item) =>
                  item.id === itemToAdd.id
                      ? {
                          ...item,
                          property_id: propertyId,
                          property_name: propertyName,
                      }
                      : item
              )
          );

          console.log(`${itemToAdd.type} mülke eklendi: ${propertyName}`);
          // TODO: Optional: Show success notification

      } catch (error) {
          console.error(`${itemToAdd.type} mülke eklenirken hata:`, error);
          alert('Öğe mülke eklenemedi. Lütfen tekrar deneyin.');
          // TODO: Optional: Show error notification
      } finally {
          setLoading(false); // Hide loading
      }
  };


  // Handles creating a new property and associating the selected menu item with it
  const handleCreateProperty = async () => {
    // Get value directly from DOM for maximum performance
    const propertyNameInput = document.getElementById('property-name-input');
    const propertyNameValue = propertyNameInput ? propertyNameInput.value.trim() : '';
    
    if (!propertyNameValue) {
      alert('Lütfen geçerli bir mülk adı girin.');
      return;
    }
    
    if (!selectedItem || !selectedItem.forMenu || !selectedItem.type) {
      console.error('Yeni mülk için ilişkilendirilecek öğe bulunamadı.');
      setCreatePropertyDialogOpen(false); // Close dialog on error
      handleMenuClose();
      return;
    }

    const itemToAssociate = { ...selectedItem }; // Work with a copy
    const currentUserId = session?.user?.id;

    if (!currentUserId) {
        alert('Mülk oluşturmak için giriş yapmalısınız.');
        setCreatePropertyDialogOpen(false);
        handleMenuClose();
        return;
    }
    
    setLoading(true); // Show loading indicator (e.g., on the dialog button)
    setCreatePropertyDialogOpen(false); // Close dialog immediately
    handleMenuClose(); // Close menu immediately

    try {
      const propertyId = uuidv4();

      // 1. Create the new property entry
      const propertyData = {
        id: propertyId,
        name: propertyNameValue, // Use the value obtained directly from DOM
        user_id: currentUserId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Set thumbnail ONLY if the associated item is an IMAGE
        thumbnail_url: itemToAssociate.type === 'image' ? itemToAssociate.thumbnailUrl : null, // Use thumbnail URL
      };

      const { error: propertyError } = await supabase
        .from('properties')
        .insert(propertyData);

      if (propertyError) throw propertyError;

      // 2. Update the associated item (image or video) with the new property ID and name
      const tableName = itemToAssociate.type === 'image' ? 'images' : 'videos';
      const { error: itemError } = await supabase
        .from(tableName)
        .update({
          property_id: propertyId,
          property_name: propertyNameValue, // Use the value obtained directly from DOM
        })
        .eq('id', itemToAssociate.id);

      // If updating the item fails, ideally we should roll back the property creation
      // For simplicity here, we log a warning and inform the user.
      if (itemError) {
          console.warn(`Mülk oluşturuldu (ID: ${propertyId}) ancak öğe (ID: ${itemToAssociate.id}) güncellenemedi:`, itemError);
          // Consider deleting the just-created property or informing the user
          throw new Error(`Mülk oluşturuldu ancak ${itemToAssociate.type} ilişkilendirilemedi. Lütfen öğeyi manuel olarak mülke ekleyin.`);
      }

      // 3. Update local state: Add new property to list and update the item
      const newProperty = { id: propertyId, name: propertyNameValue };
      setProperties(prev => [
          newProperty,
          ...prev
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))); // Match fetch order (newest first) or sort alphabetically: a.name.localeCompare(b.name)

      setItems(prev =>
        prev.map(item =>
          item.id === itemToAssociate.id
            ? { ...item, property_id: propertyId, property_name: propertyNameValue }
            : item
        )
      );

      console.log(`Yeni mülk '${propertyNameValue}' oluşturuldu ve ${itemToAssociate.type} ilişkilendirildi.`);
      setNewPropertyName(''); // Reset input field
      // TODO: Optional: Show success notification
      // Optional: Navigate to the new property page
      // navigate(`/property/${propertyId}`);

    } catch (error) {
      console.error('Mülk oluşturma veya ilişkilendirme hatası:', error);
      alert(`Mülk oluşturulamadı: ${error.message || 'Bilinmeyen bir hata oluştu.'}`);
      // TODO: Optional: Show error notification
    } finally {
      setLoading(false); // Hide loading indicator
    }
  };


  // Determine which items to display in the grid
  const itemsToDisplay = searchQuery.trim() || filterType !== 'all' ? filteredItems : items;

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        maxWidth: '1600px',
        margin: '0 auto',
        padding: { xs: '20px 15px', sm: '30px 20px' }, // Adjust padding for mobile
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        borderRadius: '24px',
        boxShadow: `0 8px 32px ${
          theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.3)'
            : 'rgba(0, 0, 0, 0.15)'
        }`,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden', // Hide potential overflow from animations/effects
        position: 'relative',
        // Keep background pseudo-element styles
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 50%),
            repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px),
            repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 2px, transparent 2px, transparent 10px)
          `,
          backgroundSize:
            '100% 100%, 20px 20px, 20px 20px',
          animation: 'gradientMove 20s ease-in-out infinite',
          zIndex: -1,
          borderRadius: 'inherit',
          opacity: 0.5,
        },
        '@keyframes gradientMove': { // Define animation if not defined globally
            '0%': { backgroundPosition: '0% 0%, 0 0, 0 0' },
            '50%': { backgroundPosition: '100% 100%, 10px 10px, -10px -10px' },
            '100%': { backgroundPosition: '0% 0%, 0 0, 0 0' },
        }
      })}
      ref={containerRef}
    >
      {/* Top Controls: Tabs, Search, Slider */}
      <Box
        sx={{
          mb: 3,
          mt: 2,
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' }, // Stack on small, row on medium+
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' }, // Stretch on small, center on medium+
          gap: 2,
        }}
      >
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"} // Allow scrolling on mobile
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            // mb: { xs: 2, md: 0 },
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0, // Prevent tabs from shrinking too much
            order: { xs: 1, md: 0 }, // Tabs first on mobile
            width: { xs: '100%', md: 'auto' }, // Full width on mobile
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: '3px', // Make indicator slightly thicker
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem', // Slightly smaller font for tabs
              minWidth: { xs: 80, sm: 100 }, // Adjust min width
              px: { xs: 1.5, sm: 2 }, // Adjust padding
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600, // Bold selected tab
              },
            },
          }}
        >
          <Tab label="Tümü" />
          <Tab label="Mülklerim" />
          <Tab label="Resimler" />
          <Tab label="Videolar" />
        </Tabs>

        {/* Search and Slider Container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' }, // Stack on xs, row on sm+
            gap: 2,
            alignItems: 'center',
            width: { xs: '100%', md: 'auto' }, // Full width on mobile/small
            order: { xs: 0, md: 1 }, // Search/slider first on medium+
            flexGrow: { xs: 0, md: 1 }, // Allow this box to grow on medium+
            justifyContent: { md: 'flex-end' } // Align controls to the right on medium+
          }}
        >
          {/* Search Input */}
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'background.paper',
              borderRadius: '8px',
              padding: '4px 12px',
              width: { xs: '100%', sm: '300px' }, // Full width on xs, fixed on sm+
              transition: 'all 0.3s ease',
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                boxShadow: `0 2px 8px ${theme.palette.primary.main}20`,
              },
              '&:focus-within': { // Highlight when input is focused
                borderColor: 'primary.main',
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
              }
            })}
          >
            <SearchIcon
              sx={{
                color: 'primary.light', // Lighter icon color
                mr: 1,
                opacity: 0.7,
              }}
            />
            <InputBase
              placeholder="Ara (başlık, prompt, mülk...)" // Updated placeholder
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth // Takes full width of its container
              sx={{
                fontSize: '0.9rem',
                '& input': {
                  color: 'text.primary',
                  '&::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.8,
                  },
                },
              }}
            />
             {searchQuery && ( // Show clear button if there's text
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ p: '4px', color: 'text.secondary' }}
                  aria-label="Clear search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
          </Box>

          {/* Images per Row Slider (Hidden on xs) */}
          {!isMobile && (
             <Box sx={{ width: '250px', display: { xs: 'none', sm: 'block' } }}> {/* Hide on xs */}
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center', // Align items vertically
                  gap: 2, // Space between label and slider
                }}
              >
                <Typography
                  id="images-per-row-slider-label" // Corrected ID for label
                  sx={{
                    color: 'text.secondary', // More subtle color
                    fontSize: '0.8rem',
                    fontWeight: 400,
                    whiteSpace: 'nowrap', // Prevent wrapping
                    flexShrink: 0, // Prevent shrinking
                  }}
                >
                  Sütun:
                </Typography>
                <Slider
                  value={imagesPerRow}
                  onChange={handleSliderChange}
                  aria-labelledby="images-per-row-slider-label" // Reference label ID
                  step={1}
                  marks={[ // Optional: Add marks for clarity
                      { value: 2, label: '2' },
                      { value: 3, label: '3' },
                      { value: 4, label: '4' },
                      { value: 5, label: '5' },
                      { value: 6, label: '6' },
                  ]}
                  min={2}
                  max={6}
                  size="small" // Use smaller slider
                  sx={{
                    flexGrow: 1, // Allow slider to take remaining space
                    color: 'primary.light',
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                      backgroundColor: 'primary.main',
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: (theme) =>
                          `0 0 0 6px ${theme.palette.primary.main}20`,
                      },
                      '&.Mui-active': {
                           boxShadow: (theme) =>
                          `0 0 0 8px ${theme.palette.primary.main}30`,
                      }
                    },
                    '& .MuiSlider-track': {
                      border: 'none',
                       height: 3,
                    },
                    '& .MuiSlider-rail': {
                       height: 3,
                      opacity: 0.3,
                    },
                     '& .MuiSlider-markLabel': {
                      fontSize: '0.7rem',
                      color: 'text.disabled'
                    },
                     '& .MuiSlider-mark': { // Hide default marks if labels are used
                        display: 'none'
                     }
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Loading Indicator or Grid */}
      {loading && items.length === 0 ? ( // Show main loader only on initial load
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1, // Take remaining space
            minHeight: 300, // Ensure it has some height
          }}
        >
          <CircularProgress />
        </Box>
      ) : itemsToDisplay.length === 0 ? (
          // No items message
          <Box sx={{ textAlign: 'center', mt: 5, color: 'text.secondary', flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                {searchQuery ? 'Sonuç Bulunamadı' : 'Galeri Boş'}
              </Typography>
              {searchQuery && <Typography>Arama kriterlerinize ('{searchQuery}') uygun sonuç bulunamadı.</Typography>}
              {filterType !== 'all' && !searchQuery && <Typography>Bu kategoride ('{filterType}') öğe bulunamadı.</Typography>}
              {!searchQuery && filterType === 'all' && <Typography>Henüz hiç görsel veya video yüklemediniz.</Typography>}
              {/* TODO: Add a button/link to the generation page? */}
          </Box>
      ) : (
        // Items Grid
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {itemsToDisplay.map(
            (item, index) => ( // Use item instead of image
              <Grid
                 item
                 xs={6} // Two columns on extra-small screens
                 sm={4} // Three columns on small screens
                 md={12 / imagesPerRow} // Dynamic columns based on slider for medium screens and up
                 key={item.id || `item-${index}`} // Use item.id, fallback to index if ID missing
               >
                {/* Wrap each item card in a Box with data-id for the observer */}
                 <Box
                    className="grid-item-observer-target" // Class for observer querySelector
                    sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}
                    data-id={item.id} // Data attribute for observer callback
                 >
                  {/* Media Container */}
                   <Box
                     sx={{
                       position: 'relative',
                       paddingTop: '75%', // Aspect ratio (4:3) - adjust if needed
                       overflow: 'hidden',
                       borderRadius: '12px 12px 0 0',
                       boxShadow: (theme) =>
                         `0 2px 8px ${
                           theme.palette.mode === 'dark'
                             ? 'rgba(0, 0, 0, 0.25)'
                             : 'rgba(0, 0, 0, 0.1)'
                         }`,
                       transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                       backgroundColor: theme.palette.action.hover, // Background for loading state
                       border: (theme) =>
                         `1px solid ${theme.palette.divider}`,
                       borderBottom: 'none',
                       cursor: 'pointer', // Indicate clickable area
                       '&:hover': {
                         transform: 'translateY(-3px)',
                         boxShadow: (theme) =>
                           `0 6px 16px ${
                             theme.palette.mode === 'dark'
                               ? 'rgba(0, 0, 0, 0.3)'
                               : 'rgba(0, 0, 0, 0.15)'
                           }`,
                       },
                     }}
                     onClick={(e) => handleItemClick(item, e)} // Attach click handler here
                   >
                     {/* Fade in content when visible */}
                    {visibleItems.includes(item.id) ? ( // Check visibleItems
                       <Fade in timeout={400}>
                         <Box
                           sx={{
                             position: 'absolute',
                             top: 0,
                             left: 0,
                             height: '100%',
                             width: '100%',
                             display: 'block', // Ensure Box takes up space
                           }}
                         >
                           {/* Render image or video thumbnail */}
                           <StyledImage
                            src={item.thumbnail_url || item.url || '/placeholder-image.png'} // Prioritize thumbnail_url, fallback to url, then placeholder
                            alt={`Oluşturulan ${item.type === 'image' ? 'resim' : 'video'} - ${item.prompt?.substring(0, 30) || index + 1}`} // More descriptive alt text
                            loading="lazy" // Native lazy loading
                           />
                           {/* Video indicator */}
                           {item.type === 'video' && (
                             <Box
                               sx={{
                                 position: 'absolute',
                                 top: 8,
                                 left: 8,
                                 backgroundColor: 'rgba(0, 0, 0, 0.65)',
                                 color: 'white',
                                 padding: '2px 6px',
                                 borderRadius: '4px',
                                 display: 'flex',
                                 alignItems: 'center',
                                 pointerEvents: 'none', // Prevent interaction
                               }}
                             >
                               <VideocamOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                               <Typography variant="caption" sx={{fontSize: '0.65rem', lineHeight: 1}}>VIDEO</Typography>
                             </Box>
                           )}
                           {/* Property Link Chip */}
                           {item.property_id && ( // Check item.property_id
                             <Box
                               sx={{
                                 position: 'absolute',
                                 top: 8,
                                 right: 8,
                                 backgroundColor:
                                   'rgba(138, 43, 226, 0.85)', // Slightly stronger color
                                 color: 'white',
                                 padding: '3px 8px',
                                 borderRadius: '10px', // Pill shape
                                 fontSize: '0.7rem',
                                 fontWeight: 500,
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 0.5,
                                 cursor: 'pointer', // Indicate clickable
                                 transition: 'background-color 0.2s ease',
                                 '&:hover': {
                                   backgroundColor:
                                     'rgba(138, 43, 226, 1)', // Darken on hover
                                 },
                               }}
                               onClick={(e) => {
                                  e.stopPropagation(); // IMPORTANT: Prevent handleItemClick on the card
                                  navigate( // Use item.property_id
                                    `/property/${item.property_id}`
                                  );
                                }}
                                title={`Mülke Git: ${item.property_name || 'İsimsiz Mülk'}`} // Tooltip for property name
                             >
                               <HomeIcon sx={{ fontSize: 14 }} /> {/* Smaller icon */}
                               {/* Truncate long property names */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: 'inherit',
                                    fontWeight: 'inherit',
                                    display: 'inline-block',
                                    maxWidth: '100px', // Limit width
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {item.property_name || 'Mülk'}
                                </Typography>
                             </Box>
                           )}
                         </Box>
                       </Fade>
                     ) : (
                        // Placeholder shown before item becomes visible
                        <Box sx={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: theme.palette.action.hover}}>
                           {/* Optional: Small spinner or just background */}
                           {/* <CircularProgress size={20} thickness={4} /> */}
                        </Box>
                     )}
                   </Box>
                   {/* Item Footer: Date and Menu Button */}
                   <Box
                     sx={{
                       display: 'flex',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       padding: '8px 12px',
                       backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.9)' : '#fafafa', // Slightly adjusted colors
                       borderRadius: '0 0 12px 12px',
                       borderTop: 'none',
                       border: theme => `1px solid ${theme.palette.divider}`,
                       borderTopWidth: 0, // Ensure no double border
                       boxShadow: theme => `0 1px 3px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.05)'}`, // Softer shadow
                     }}
                   >
                     {/* Date */}
                     <Typography
                       variant="caption"
                       sx={{
                         color: 'text.secondary',
                         fontWeight: 400, // Regular weight
                         fontSize: '0.75rem',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                         maxWidth: 'calc(100% - 40px)', // Ensure space for button
                       }}
                       title={item.created_at ? new Date(item.created_at).toLocaleString() : 'Tarih Yok'} // Show full date on hover
                     >
                       {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Tarih Yok'} {/* Use item.created_at */}
                     </Typography>
                     {/* Menu Button */}
                     <IconButton
                       onClick={(e) => handleMenuOpen(e, item)} // Pass item
                       className="menu-button" // Class for identification in handleItemClick
                       size="small"
                       sx={{
                         color: 'text.secondary', // Use secondary text color for subtlety
                         p: '4px', // Smaller padding
                         '&:hover': {
                           backgroundColor: theme => theme.palette.action.hover, // Standard hover
                           color: 'primary.main', // Highlight on hover
                         },
                         pointerEvents: 'auto' // Make sure button is clickable
                       }}
                       aria-label={`Actions for item ${item.id}`}
                       aria-controls={Boolean(anchorEl) ? 'item-action-menu' : undefined}
                       aria-haspopup="true"
                       aria-expanded={Boolean(anchorEl) && selectedItem?.id === item.id && selectedItem?.forMenu ? 'true' : undefined} // Check forMenu flag too
                     >
                       <MoreVertIcon fontSize="small" />
                     </IconButton>
                   </Box>
                 </Box>
               </Grid>
            )
          )}
        </Grid>
      )}

      {/* Item Action Menu */}
      <Menu
        id="item-action-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{
            'aria-labelledby': 'item-action-menu-button', // Reference the button if possible (needs ID on IconButton)
        }}
        PaperProps={{
          elevation: 3, // Add slight elevation
          sx: {
            mt: 1, // Margin top from anchor
            width: 230, // Slightly wider menu
            overflow: 'visible', // Allow potential shadows/borders outside
            // Optional: Add a small triangle pointing to the anchor
            // '&::before': { ... },
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              px: 2,
              py: 1.2, // Adjust padding
              borderRadius: '6px', // Slightly less rounded
              mb: 0.5,
              '&:last-of-type': { mb: 0 }, // No margin bottom on last item
              '& .MuiListItemIcon-root': { // Style icons
                  minWidth: 32, // Adjust icon spacing
                  color: 'text.secondary'
              },
               '&:hover': {
                 backgroundColor: theme.palette.action.hover // Standard hover
               }
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }} // Origin for animations
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
          {/* View Item */}
         <MenuItem onClick={(e) => {
           e.stopPropagation(); // Prevent unwanted side effects
           if (selectedItem?.forMenu) { // Ensure it's the item from the menu
             handleItemClick(selectedItem, null); // Trigger modal opening, pass null event
           }
           handleMenuClose(); // Close menu after action initiated
         }}>
           <ListItemIcon>
             <VisibilityIcon fontSize="small" />
           </ListItemIcon>
           <ListItemText>Görüntüle</ListItemText>
         </MenuItem>

         {selectedItem?.type === 'image' && (
           <MenuItem onClick={() => {
             if (selectedItem?.id) {
               navigate(`/editor/${selectedItem.id}`);
             }
             handleMenuClose();
           }}>
             <ListItemIcon>
               <EditIcon fontSize="small" />
             </ListItemIcon>
             <ListItemText>Düzenle</ListItemText>
           </MenuItem>
         )}

        {/* Go to Property (if linked) OR Add to Property options */}
        {selectedItem?.property_id ? ( // Check selectedItem (must exist if menu is open and marked forMenu)
          <MenuItem onClick={() => {
             if (selectedItem?.property_id) {
                navigate(`/property/${selectedItem.property_id}`); // Use selectedItem
             }
             handleMenuClose();
          }}>
            <ListItemIcon>
              <HomeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mülke Git ({selectedItem?.property_name ? `'${selectedItem.property_name.substring(0,15)}${selectedItem.property_name.length > 15 ? '...' : ''}'` : ''})</ListItemText>
          </MenuItem>
        ) : (
          // Show options to link if not already linked
          <>
            {/* Create New Property */}
            <MenuItem onClick={() => {
              // Open the dialog, but don't close the menu yet
              // The dialog's action will close the menu upon success/cancel
              if (selectedItem?.forMenu) { // Ensure we have the item context
                  setCreatePropertyDialogOpen(true);
                  // Keep menu open until dialog interaction completes
              } else {
                  console.error("Cannot create property without a selected item context.");
                  handleMenuClose(); // Close menu if context is lost
              }
            }}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Yeni Mülk Oluştur</ListItemText>
            </MenuItem>

            {/* Add to Existing Property (if properties exist) */}
            {properties.length > 0 && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography key="prop-title" variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 500 }}>
                  Mevcut Mülke Ekle:
                </Typography>
                {/* Limit number of properties shown directly in menu or add scroll */}
                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}> {/* Add scroll if many properties */}
                  {properties.map(property => ( // Show all properties with scroll
                  <MenuItem
                    key={property.id}
                    onClick={() => {
                      if (selectedItem?.forMenu) {
                          handleAddToProperty(property.id, property.name);
                      }
                      // handleAddToProperty closes the menu internally
                    }}
                    sx={{ pl: 2 }} // Indent slightly less
                  >
                    <ListItemIcon>
                      <HomeIcon fontSize="small" sx={{ ml: 0.5 }}/> {/* Use Home icon */}
                    </ListItemIcon>
                    {/* Truncate long property names in menu */}
                    <ListItemText
                        primary={property.name}
                        primaryTypographyProps={{
                            style: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '150px' // Adjust max width as needed
                            }
                        }}
                        title={property.name} // Show full name on hover
                    />
                  </MenuItem>
                    ))}
                </Box>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
             {properties.length === 0 && selectedItem?.forMenu && ( // Message if no properties exist yet
               <Typography key="no-props" variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.disabled', fontStyle: 'italic' }}>
                 (Henüz mevcut mülk yok)
               </Typography>
             )}
          </>
        )}

        {/* Delete Item */}
        <MenuItem
          onClick={(e) => {
            if (selectedItem?.forMenu) { // Check selectedItem (must exist and be for menu)
              handleDeleteItem(selectedItem, e); // Use handleDeleteItem, pass the item object
            } else {
               console.error('Silinecek öğe menü bağlamında bulunamadı.');
               handleMenuClose(); // Close menu if context is invalid
            }
            // handleDeleteItem closes the menu internally after confirmation
          }}
          sx={{ color: 'error.main' }} // Use theme's error color
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sil</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Property Dialog */}
      <Dialog
        open={createPropertyDialogOpen}
        onClose={() => {
            setCreatePropertyDialogOpen(false);
            // No need to reset state since we're using uncontrolled input
            handleMenuClose(); // Ensure menu is closed if dialog is cancelled
        }}
        PaperProps={{
          component: 'form', // Make it a form for potential Enter key submission
          onSubmit: (e) => {
              e.preventDefault(); // Prevent default form submission
              handleCreateProperty(); // Call the handler on submit
          },
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            width: '90%', // Responsive width
            maxWidth: '450px' // Max width
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Yeni Mülk Oluştur</DialogTitle>
        <DialogContent>
          {/* Show selected item thumbnail and info */}
          {selectedItem?.forMenu ? ( // Check selectedItem (should be the one from the menu)
            <>
              <Box sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}>
                {/* Thumbnail */}
                <Box sx={{
                  width: '80px', // Slightly smaller thumbnail
                  height: '80px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  flexShrink: 0,
                  position: 'relative', // For video icon overlay
                  backgroundColor: theme.palette.action.hover, // Background for loading/placeholder
                }}>
                  <img
                    src={selectedItem.thumbnailUrl || '/placeholder-image.png'} // Use thumbnail for both image/video, provide fallback
                    alt="Seçilen öğe"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  {selectedItem.type === 'video' && (
                     <VideocamOutlinedIcon sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        fontSize: 18,
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        borderRadius: '2px',
                        padding: '1px'
                     }} />
                  )}
                </Box>
                {/* Info Text */}
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Seçilen {selectedItem.type === 'image' ? 'resim' : 'video'} için yeni bir mülk oluşturulacak ve bu öğe mülke eklenecektir.
                  </Typography>
                  {selectedItem.id && ( // Check selectedItem.id
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {selectedItem.type === 'image' ? 'Resim' : 'Video'} ID: <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{selectedItem.id.substring(0,8)}...</Box> {/* Show truncated ID */}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} /> {/* Separator */}
            </>
          ) : (
            // This case should ideally not happen if dialog is opened correctly
            <Alert severity="warning" sx={{ mb: 2 }}>
              Mülk oluşturmak için ilişkilendirilecek bir öğe seçilmedi veya bulunamadı.
            </Alert>
          )}
          {/* Property Name Input - Completely uncontrolled for maximum performance */}
          <Box sx={{ 
            position: 'relative',
            mb: 1
          }}>
            <Typography 
              variant="body2" 
              component="label" 
              htmlFor="property-name-input"
              sx={{ 
                display: 'block', 
                mb: 1, 
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              Mülk Adı *
            </Typography>
            <input
              id="property-name-input"
              type="text"
              autoFocus
              disabled={loading || !selectedItem?.forMenu}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : '#fff',
                color: theme.palette.text.primary,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.palette.primary.main;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = theme.palette.divider;
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt:1 }}> {/* Adjust padding */}
          <Button
            onClick={() => {
                setCreatePropertyDialogOpen(false);
                // No need to reset state since we're using uncontrolled input
                handleMenuClose(); // Ensure menu closed
            }}
            disabled={loading} // Disable while loading
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            İptal
          </Button>
          <Button
            type="submit" // Submit the form
            variant="contained"
            disabled={loading || !selectedItem?.forMenu} // Remove newPropertyName check - we'll validate on submit
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              px: 3
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} /> {/* Inherit color */}
                Oluşturuluyor...
              </>
            ) : (
              'Oluştur ve Ekle'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Detail Modal (for Image or Video) */}
      <Modal
        open={!!selectedItem && !selectedItem.forMenu} // Simplified open condition using Fade exit animation
        onClose={(event, reason) => {
          // Prevent closing on backdrop click if loading or comparing
           if (reason === 'backdropClick' && (loadingFullImage || showCompare)) {
             return;
           }
           handleCloseModal();
        }}
        aria-labelledby="item-modal-title" // Use generic ID
        aria-describedby="item-modal-description"
        closeAfterTransition
        // Add backdrop styling for blur effect
         BackdropProps={{
           timeout: 300,
           sx: { backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.5)' }
         }}
      >
        <Fade in={!!selectedItem && !selectedItem.forMenu} timeout={300}>
           {/* Modal Content Box */}
           <Box sx={{
             position: 'absolute',
             top: '50%',
             left: '50%',
             width: { xs: '95%', sm: '90%' }, // Responsive width
             maxWidth: 1000,
             maxHeight: '90vh',
             bgcolor: 'background.paper',
             transform: 'translate(-50%, -50%)',
             willChange: 'transform', // Optimize animation
             boxShadow: theme => `0 12px 40px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.2)'}`, // Stronger shadow
             p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
             borderRadius: '16px',
             overflowY: 'auto', // Allow vertical scrolling ONLY if content exceeds maxHeight
             border: theme => `1px solid ${theme.palette.divider}`,
             background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
           }}>
             {/* Close Button */}
             <IconButton
               onClick={handleCloseModal}
               sx={{
                 position: 'absolute',
                 right: { xs: 8, sm: 12 }, // Adjust position based on padding
                 top: { xs: 8, sm: 12 },
                 zIndex: 10, // Ensure it's above content
                 color: 'text.secondary', // Use secondary color
                 backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                 '&:hover': {
                   backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                   color: 'text.primary',
                 },
               }}
               aria-label="Close modal"
             >
               <CloseIcon />
             </IconButton>

             {/* Modal Body */}
             <Box sx={{
               position: 'relative', // Needed for absolute positioned loader
               '& .MuiCircularProgress-root': { // Style loader if needed
                 color: 'primary.main'
               }
             }}>
               {/* Loading Indicator for Full Media */}
               {loadingFullImage && (
                 <Box sx={{
                   position: 'absolute', // Overlay on top of media area
                   top: 0,
                   left: 0,
                   right: 0,
                   bottom: 0,
                   display: 'flex',
                   justifyContent: 'center',
                   alignItems: 'center',
                   // Match media container height approximately
                   minHeight: { xs: '40vh', sm: '50vh', md: '60vh'},
                   backgroundColor: 'rgba(0, 0, 0, 0.1)', // Slight overlay
                   backdropFilter: 'blur(2px)',
                   zIndex: 5, // Above media, below close button
                   borderRadius: '12px' // Match media rounding
                 }}>
                   <CircularProgress />
                 </Box>
               )}

               {/* Conditional Rendering for Image or Video */}
               {/* Image Display (Simplified: Use CardMedia for all images) */}
               {selectedItem?.type === 'image' && selectedItem?.url && (
                 <CardMedia
                   component="img"
                   image={selectedItem.url} // Use 'image' prop for CardMedia
                   alt={selectedItem.prompt || 'Görüntü'}
                   sx={{
                     display: 'block', // Ensure it behaves like a block element
                     width: '100%', // Take full width
                     maxHeight: { xs: '60vh', sm: '70vh' }, // Responsive max height
                     objectFit: 'contain', // Ensure whole image is visible
                     borderRadius: '12px', // Rounded corners
                     mb: 3, // Margin bottom
                     visibility: loadingFullImage ? 'hidden' : 'visible', // Hide while loading
                     backgroundColor: theme.palette.action.hover, // Background while loading
                   }}
                   onLoad={handleImageLoad} // Callback when image fully loads
                   onError={(e) => {
                       console.error("Error loading image:", selectedItem.url, e);
                       setLoadingFullImage(false); // Stop loading even if image fails
                       // Optionally show a placeholder/error state for the image
                       e.target.alt = "Görüntü yüklenemedi";
                       e.target.src = '/placeholder-error.png'; // Provide an error placeholder image
                   }}
                 />
               )}
               {/* Video Display: Handle .webp as image, others as video */}
               {selectedItem?.type === 'video' && selectedItem?.url && (() => {
                 // Attempt to correct potential typo in URL path
                 const originalUrl = selectedItem.url;
                 const correctedVideoUrl = originalUrl.includes('/generateddeos/')
                   ? originalUrl.replace('/generateddeos/', '/generated-videos/')
                   : originalUrl;

                 const isWebP = correctedVideoUrl.toLowerCase().endsWith('.webp');

                 if (isWebP) {
                   // Render .webp files using an <img> tag
                   return (
                     <>
                       {/* Removed the notification about WebP format */}
                       <CardMedia
                         component="img" // Use img component for WebP
                         image={correctedVideoUrl} // Use 'image' prop
                         alt={selectedItem.prompt || 'Video Animasyonu'}
                         sx={{
                           display: 'block',
                           width: '100%',
                           maxHeight: { xs: '60vh', sm: '70vh' },
                           objectFit: 'contain',
                           borderRadius: '12px',
                           mb: 3,
                           visibility: loadingFullImage ? 'hidden' : 'visible',
                           backgroundColor: theme.palette.action.hover,
                         }}
                         onLoad={handleImageLoad} // Use the same load handler
                         onError={(e) => {
                           console.error(`Error loading WebP image (intended as video): ${correctedVideoUrl}`, e);
                           setLoadingFullImage(false);
                           e.target.alt = "Animasyon yüklenemedi";
                           e.target.src = '/placeholder-error.png';
                         }}
                       />
                     </>
                   );
                 } else {
                   // Render standard video formats using <video> tag
                   return (
                     <CardMedia
                       component="video"
                       src={correctedVideoUrl} // Use the potentially corrected URL
                       controls // Show native video controls
                       autoPlay={false} // Do not autoplay in modal
                       playsInline // Important for mobile playback
                   preload="metadata" // Load enough to show duration/first frame
                   sx={{
                      display: 'block', // Ensure it behaves like a block element
                      width: '100%',
                      maxHeight: { xs: '60vh', sm: '70vh' },
                      borderRadius: '12px',
                      mb: 3,
                      backgroundColor: '#000', // Black background often looks better for videos
                      outline: 'none', // Remove focus outline if needed
                      visibility: loadingFullImage ? 'hidden' : 'visible' // Hide while loading
                   }}
                   onLoadedData={handleImageLoad} // Use handleImageLoad when video metadata/data is ready
                   onError={(e) => {
                       const originalUrl = selectedItem.url;
                       const attemptedUrl = e.target.src; // The URL that failed (could be original or corrected)
                       console.error(`Error loading video. Original URL: ${originalUrl}, Attempted URL: ${attemptedUrl}`, e);
                       setLoadingFullImage(false); // Stop loading on error
                       // Optionally show a placeholder/error state for the video
                       // Consider adding a user-facing error message here as well
                       e.target.poster = '/placeholder-error.png'; // Show an error poster
                     }}
                     />
                   );
                 }
               })()}

               {/* Details Section */}
               {/* Render details only if an item is selected (handles edge cases) */}
               {selectedItem && !selectedItem.forMenu && ( // Ensure it's not the menu item state
                   <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                       {/* Header: Title and Action Buttons */}
                       <Box sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center',
                           mb: 2, // Margin below header
                           pb: 1.5, // Padding below header content
                           borderBottom: theme => `1px solid ${theme.palette.divider}`, // Separator line
                       }}>
                           {/* Dynamic Title */}
                           <Typography
                               id="item-modal-title" // Link for accessibility
                               variant="h6"
                               component="h2" // Semantic heading
                               sx={{
                                   color: 'primary.main',
                                   fontWeight: 600,
                                   fontSize: { xs: '1.1rem', sm: '1.25rem' } // Responsive font size
                               }}
                           >
                               {selectedItem?.type === 'image' ? 'Resim Detayları' : 'Video Detayları'}
                           </Typography>
                           {/* Action Buttons: Compare (Image only), Expand Metadata */}
                           <Box sx={{ display: 'flex', gap: 1 }}>
                               {/* Compare Button (Only for images with an original) */}
                               {selectedItem?.type === 'image' && selectedItem?.original_image_url && (
                                   <CustomTooltip title="Orijinal ile Karşılaştır">
                                       <IconButton
                                           onClick={() => {
                                               setShowCompare(true);
                                               // Keep main modal open when compare opens
                                           }}
                                           size="small"
                                           sx={{
                                                // Style subtle button
                                               backgroundColor: theme => theme.palette.action.hover,
                                               color: 'text.secondary',
                                               '&:hover': {
                                                   backgroundColor: theme => theme.palette.action.selected,
                                                   color: 'primary.main'
                                               }
                                           }}
                                           aria-label="Compare with original image"
                                       >
                                           <CompareIcon fontSize="small" />
                                       </IconButton>
                                   </CustomTooltip>
                               )}
                               {/* Expand Metadata Button */}
                               <ExpandMore
                                   expand={expandedMetadata}
                                   onClick={() => setExpandedMetadata(!expandedMetadata)}
                                   aria-expanded={expandedMetadata}
                                   aria-label={expandedMetadata ? "Detayları Gizle" : "Detayları Göster"}
                                   size="small"
                                   sx={{
                                       // Style subtle button
                                       backgroundColor: theme => theme.palette.action.hover,
                                       color: 'text.secondary',
                                        '&:hover': {
                                           backgroundColor: theme => theme.palette.action.selected,
                                           color: 'primary.main'
                                       }
                                   }}
                               >
                                   <ExpandMoreIcon fontSize="small" />
                               </ExpandMore>
                           </Box>
                       </Box>

                       {/* Collapsible Metadata Section */}
                       <Collapse in={expandedMetadata} timeout="auto" unmountOnExit>
                           <Box sx={{
                               // Use a slightly different background for the details section
                               backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                               borderRadius: '10px', // Rounded corners for the section
                               p: { xs: 1.5, sm: 2 }, // Padding inside the section
                               mb: 2 // Margin below the section
                           }}>
                               {/* Prompt (for images) or Title (for videos) */}
                               {/* Use 'prompt' field which holds title for videos */}
                               {selectedItem?.prompt && (
                                   <Box sx={{ mb: 2 }}>
                                       <Typography variant="subtitle2" sx={{ color: 'primary.light', mb: 0.5, fontWeight: 600 }}>
                                           {selectedItem.type === 'image' ? metadataTranslations['Prompt'] || 'Prompt' : 'Başlık'}
                                       </Typography>
                                       <Box sx={{
                                           display: 'flex',
                                           justifyContent: 'space-between',
                                           alignItems: 'flex-start', // Align copy button to top right
                                           backgroundColor: 'background.paper', // Contrast background
                                           p: 1.5,
                                           borderRadius: '8px',
                                           border: theme => `1px solid ${theme.palette.divider}`,
                                       }}>
                                           <Typography variant="body2" sx={{ color: 'text.primary', flexGrow: 1, mr: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                               {selectedItem.prompt}
                                           </Typography>
                                           <IconButton
                                               size="small"
                                               onClick={() => copyToClipboard(selectedItem.prompt)}
                                               aria-label="Copy prompt"
                                               sx={{ color: 'text.secondary', p: '4px' }}
                                           >
                                               <ContentCopyIcon fontSize="small" />
                                           </IconButton>
                                       </Box>
                                   </Box>
                               )}

                               {/* Negative Prompt (Only for images) */}
                               {selectedItem?.type === 'image' && selectedItem?.negative_prompt && (
                                   <Box sx={{ mb: 2 }}>
                                       <Typography variant="subtitle2" sx={{ color: 'primary.light', mb: 0.5, fontWeight: 600 }}>
                                           {metadataTranslations['Negative Prompt'] || 'Negative Prompt'}
                                       </Typography>
                                        <Box sx={{
                                           display: 'flex',
                                           justifyContent: 'space-between',
                                           alignItems: 'flex-start',
                                           backgroundColor: 'background.paper',
                                           p: 1.5,
                                           borderRadius: '8px',
                                           border: theme => `1px solid ${theme.palette.divider}`,
                                       }}>
                                           <Typography variant="body2" sx={{ color: 'text.primary', flexGrow: 1, mr: 1, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                               {selectedItem.negative_prompt}
                                           </Typography>
                                           <IconButton
                                               size="small"
                                               onClick={() => copyToClipboard(selectedItem.negative_prompt)}
                                               aria-label="Copy negative prompt"
                                               sx={{ color: 'text.secondary', p: '4px' }}
                                           >
                                               <ContentCopyIcon fontSize="small" />
                                           </IconButton>
                                       </Box>
                                   </Box>
                               )}

                               {/* Grid for other metadata fields */}
                               <Grid container spacing={2}>
                                   {/* Image-Specific Fields */}
                                   {selectedItem?.type === 'image' && selectedItem?.workflow_title && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               {metadataTranslations['Workflow'] || 'Workflow'}
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {selectedItem.workflow_title}
                                           </Typography>
                                       </Grid>
                                   )}
                                   {selectedItem?.type === 'image' && selectedItem?.style && (
                                       <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               {metadataTranslations['Style'] || 'Style'}
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {metadataTranslations[selectedItem.style] || selectedItem.style}
                                           </Typography>
                                       </Grid>
                                   )}
                                   {selectedItem?.type === 'image' && selectedItem?.mode && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               {metadataTranslations['Mode'] || 'Mode'}
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {metadataTranslations[selectedItem.mode] || selectedItem.mode}
                                           </Typography>
                                       </Grid>
                                   )}
                                   {/* Add other image fields like seed, guidance_scale etc. if available */}
                                   {selectedItem?.type === 'image' && typeof selectedItem?.seed !== 'undefined' && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               {metadataTranslations['Seed'] || 'Seed'}
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {selectedItem.seed}
                                           </Typography>
                                       </Grid>
                                   )}

                                   {/* Video-Specific Fields */}
                                   {selectedItem?.type === 'video' && selectedItem?.camera_movement && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               Kamera Hareketi
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {selectedItem.camera_movement}
                                           </Typography>
                                       </Grid>
                                   )}
                                   {selectedItem?.type === 'video' && selectedItem?.duration && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               Süre
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {selectedItem.duration} saniye
                                           </Typography>
                                       </Grid>
                                   )}
                                   {/* Add other video fields like fps, motion_score etc. if available */}

                                   {/* Common Fields */}
                                   {selectedItem?.created_at && (
                                       <Grid item xs={12} sm={6}>
                                           <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                               {metadataTranslations['Created At'] || 'Oluşturulma Tarihi'}
                                           </Typography>
                                           <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                               {new Date(selectedItem.created_at).toLocaleString('tr-TR')} {/* Locale specific format */}
                                           </Typography>
                                       </Grid>
                                   )}
                                    {/* Display Linked Property Name */}
                                    {selectedItem?.property_id && selectedItem?.property_name && (
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                                Bağlı Mülk
                                            </Typography>
                                             {/* Make property name clickable */}
                                             <Button
                                                size="small"
                                                startIcon={<HomeIcon fontSize="inherit" />}
                                                onClick={() => navigate(`/property/${selectedItem.property_id}`)}
                                                sx={{
                                                    p: 0, // Remove padding
                                                    textTransform: 'none', // Keep original case
                                                    justifyContent: 'flex-start', // Align left
                                                    color: 'primary.main',
                                                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } // No hover background, add underline
                                                }}
                                                title={`Mülke Git: ${selectedItem.property_name}`}
                                            >
                                               <Typography variant="body2" sx={{ color: 'inherit' }}>
                                                 {selectedItem.property_name}
                                               </Typography>
                                            </Button>
                                        </Grid>
                                    )}
                                     {/* Add more common or specific fields here */}
                                     {/* Example: Item ID */}
                                     {selectedItem?.id && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mb: 0.2 }}>
                                                ID
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                {selectedItem.id}
                                            </Typography>
                                        </Grid>
                                     )}

                               </Grid>
                           </Box>
                       </Collapse>
                   </CardContent>
               )}
             </Box>
           </Box>
        </Fade>
      </Modal>

       {/* Image Compare Modal (Only shown for images) */}
       <Modal
         open={showCompare}
         onClose={() => setShowCompare(false)} // Simple close action
         aria-labelledby="compare-modal-title"
          BackdropProps={{ // Use backdrop for consistency
           timeout: 300,
           sx: { backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.5)' }
         }}
         sx={{
             // Ensure compare modal appears above the main modal if both are somehow open
             zIndex: (theme) => theme.zIndex.modal + 1,
         }}
       >
         {/* Box container is needed for Modal content */}
         {/* The ImageCompare component likely handles its own layout and styling */}
         <Box>
           {/* Conditionally render ImageCompare only if showCompare is true AND selectedItem is an image with original_image_url */}
           {showCompare && selectedItem?.type === 'image' && selectedItem?.original_image_url && selectedItem?.url && (
             <ImageCompare
               originalImage={selectedItem.original_image_url}
               generatedImage={selectedItem.url}
               onClose={() => setShowCompare(false)} // Pass close handler
               // Add title or other props if needed by ImageCompare
               title="Orijinal ve Oluşturulan Karşılaştırması"
             />
           )}
           {/* Handle case where compare is triggered but data is missing */}
           {showCompare && !(selectedItem?.type === 'image' && selectedItem?.original_image_url && selectedItem?.url) && (
               <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, textAlign: 'center' }}>
                   <Typography color="error">Karşılaştırma için gerekli resim verileri bulunamadı.</Typography>
                   <Button onClick={() => setShowCompare(false)} sx={{ mt: 2 }}>Kapat</Button>
               </Box>
           )}
         </Box>
       </Modal>
    </Box>
  );
};

export default Gallery;
